import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Button,
  Form,
  Alert,
  Tab,
  Card,
} from "react-bootstrap";
import { FaPlus, FaArrowLeft, FaAngleDown, FaAngleUp, FaTrash } from "react-icons/fa";
import swal from "sweetalert";
import Side from "../Sidebar";
import Top from "../Topbar";
import Loader from "../LoaderModal";
import MedicineListEditor from "./MedicineListEditor";
import MedicineListItemsTable, {
  emptyItemDraft,
  missingFields,
} from "./MedicineListItemsTable";
import * as MedicineTagListService from "../../lib/MedicineTagListService";
import * as MedicineMasterData from "../../lib/MedicineMasterData";
import { MedicineTagListOutcome } from "../../lib/MedicineTagListResponse";
import C from "../../lib/Config";

export default class MedicineListMaster extends Component {
  constructor(props) {
    super(props);
    this.state = {
      institutes: [],
      selectedInstituteId: null,
      tags: [],
      selectedTagId: null,
      expandedTagIds: new Set(),
      loading: false,
      error: null,
      showEditor: false,
      editorMode: "create", // "create" or "edit"
      editingTag: null,
      medicines: [],
      doses: [],
      whens: [],
      loadingMedicines: false,
      loadingDoses: false,
      loadingWhens: false,
      inlineEditTagId: null,
      inlineEditItemId: null,
      inlineEditDraft: emptyItemDraft(),
      inlineSavingItem: false,
    };
  }

  componentDidMount() {
    this.loadUserInstitutes();
    this.preloadMasterData();
  }

  loadUserInstitutes = async () => {
    try {
      this.setState({ loading: true });
      const userId = localStorage.getItem("userId");
      if (!userId) {
        this.setState({
          error: "User ID not found. Please log in again.",
          loading: false,
        });
        return;
      }

      const res = await fetch(
        C.getUrl() + `/institute/getAllInstituteByUserId/${userId}`
      );
      const text = await res.text();

      if (!text) {
        this.setState({
          institutes: [],
          selectedInstituteId: null,
          loading: false,
        });
        return;
      }

      try {
        const data = JSON.parse(text);
        const institutes = Array.isArray(data)
          ? data
          : data.message
          ? JSON.parse(data.message)
          : [];

        if (institutes.length === 0) {
          this.setState({
            institutes: [],
            error: "No institute is linked to your account.",
            loading: false,
          });
        } else if (institutes.length === 1) {
          // Auto-select single institute
          this.setState({
            institutes,
            selectedInstituteId: institutes[0].instituteId,
            error: null,
            loading: false,
          }, () => {
            this.loadMedicineListsForInstitute(institutes[0].instituteId);
          });
        } else {
          // Multiple institutes: show picker, user must select
          this.setState({
            institutes,
            selectedInstituteId: null,
            error: null,
            loading: false,
          });
        }
      } catch (parseErr) {
        this.setState({
          institutes: [],
          error: "Failed to parse institute data.",
          loading: false,
        });
      }
    } catch (error) {
      this.setState({
        error: "Error loading institutes. Please refresh the page.",
        loading: false,
      });
      console.error("Error loading institutes:", error);
    }
  };

  handleInstituteChange = (e) => {
    const instituteId = Number(e.target.value);
    this.setState(
      {
        selectedInstituteId: instituteId,
        selectedTagId: null,
        expandedTagIds: new Set(),
        tags: [],
        error: null,
        inlineEditTagId: null,
        inlineEditItemId: null,
        inlineEditDraft: emptyItemDraft(),
        inlineSavingItem: false,
      },
      () => {
        this.loadMedicineListsForInstitute(instituteId);
      }
    );
  };

  preloadMasterData = async () => {
    const medicines = await MedicineMasterData.getAllMedicines();
    const doses = await MedicineMasterData.getAllDoses();
    const whens = await MedicineMasterData.getAllWhens();
    this.setState({ medicines, doses, whens });
  };

  handleLoadMedicines = async () => {
    const { medicines, loadingMedicines } = this.state;
    if (medicines.length > 0 || loadingMedicines) return;

    this.setState({ loadingMedicines: true });
    const data = await MedicineMasterData.getAllMedicines();
    this.setState({ medicines: data, loadingMedicines: false });
  };

  handleLoadDoses = async () => {
    const { doses, loadingDoses } = this.state;
    if (doses.length > 0 || loadingDoses) return;

    this.setState({ loadingDoses: true });
    const data = await MedicineMasterData.getAllDoses();
    this.setState({ doses: data, loadingDoses: false });
  };

  handleLoadWhens = async () => {
    const { whens, loadingWhens } = this.state;
    if (whens.length > 0 || loadingWhens) return;

    this.setState({ loadingWhens: true });
    const data = await MedicineMasterData.getAllWhens();
    this.setState({ whens: data, loadingWhens: false });
  };

  handleCreateMedicine = async (name) => {
    const created = await MedicineMasterData.saveMedicine(name);
    if (created) {
      this.setState((prev) => ({
        medicines: [...prev.medicines, created].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
    }
    return created;
  };

  handleCreateDose = async (name) => {
    const created = await MedicineMasterData.saveDose(name);
    if (created) {
      this.setState((prev) => ({
        doses: [...prev.doses, created].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
    }
    return created;
  };

  handleCreateWhen = async (name) => {
    const created = await MedicineMasterData.saveWhen(name);
    if (created) {
      this.setState((prev) => ({
        whens: [...prev.whens, created].sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
      }));
    }
    return created;
  };

  loadMedicineListsForInstitute = async (instituteId, preserveExpansion = false) => {
    this.setState({ loading: true, error: null });
    const result = await MedicineTagListService.getByInstitute(instituteId);

    if (result.outcome === MedicineTagListOutcome.SUCCESS) {
      const tags = Array.isArray(result.data) ? result.data : [];
      this.setState((prev) => {
        if (!preserveExpansion) {
          return {
            tags,
            loading: false,
            selectedTagId: null,
            expandedTagIds: new Set(),
            inlineEditTagId: null,
            inlineEditItemId: null,
            inlineEditDraft: emptyItemDraft(),
            inlineSavingItem: false,
          };
        }
        const stillPresentIds = new Set(tags.map((t) => t.medicineTagId));
        const prunedExpanded = new Set(
          [...prev.expandedTagIds].filter((id) => stillPresentIds.has(id))
        );
        const prunedSelected = stillPresentIds.has(prev.selectedTagId)
          ? prev.selectedTagId
          : null;
        const inlineStillValid =
          prev.inlineEditTagId !== null && stillPresentIds.has(prev.inlineEditTagId);
        return {
          tags,
          loading: false,
          expandedTagIds: prunedExpanded,
          selectedTagId: prunedSelected,
          ...(inlineStillValid
            ? {}
            : {
                inlineEditTagId: null,
                inlineEditItemId: null,
                inlineEditDraft: emptyItemDraft(),
                inlineSavingItem: false,
              }),
        };
      });
    } else {
      this.setState({
        loading: false,
        error: result.message || "Failed to load medicine lists.",
        tags: [],
      });
    }
  };

  handleSelectTag = (e) => {
    const tagId = Number(e.target.value);
    this.setState({ selectedTagId: tagId });
  };

  handleToggleExpand = (tagId) => {
    this.setState((prev) => {
      const newExpanded = new Set(prev.expandedTagIds);
      if (newExpanded.has(tagId)) {
        newExpanded.delete(tagId);
      } else {
        newExpanded.add(tagId);
      }
      return { expandedTagIds: newExpanded };
    });
  };

  handleCreateList = () => {
    this.setState({
      showEditor: true,
      editorMode: "create",
      editingTag: null,
    });
  };

  handleEditList = () => {
    const { tags, selectedTagId } = this.state;
    const tag = tags.find((t) => t.medicineTagId === selectedTagId);
    if (!tag) return;

    this.setState({
      showEditor: true,
      editorMode: "edit",
      editingTag: tag,
    });
  };

  handleRemoveList = (tag) => {
    swal({
      title: "Remove medicine list?",
      text: `Remove "${tag.tagName}" and all ${tag.items?.length || 0} medicine(s) inside it?`,
      icon: "warning",
      buttons: ["Cancel", "Remove"],
    }).then((isConfirm) => {
      if (!isConfirm) return;

      MedicineTagListService.deactivateTag(tag.medicineTagId).then((result) => {
        if (result.outcome === MedicineTagListOutcome.SUCCESS) {
          swal({
            title: "Success!",
            text: `"${tag.tagName}" removed`,
            icon: "success",
            buttons: false,
            timer: 2000,
          });
          // Refresh the list
          this.loadMedicineListsForInstitute(this.state.selectedInstituteId);
        } else {
          swal({
            title: "Error",
            text: result.message || "Failed to remove list",
            icon: "error",
            buttons: false,
            timer: 2000,
          });
        }
      });
    });
  };

  handleEditorSave = () => {
    this.setState({ showEditor: false, editingTag: null });
    this.loadMedicineListsForInstitute(this.state.selectedInstituteId);
  };

  handleEditorClose = () => {
    this.setState({ showEditor: false, editingTag: null });
  };

  handleInlineDraftChange = (field, value) => {
    this.setState((prev) => ({
      inlineEditDraft: {
        ...prev.inlineEditDraft,
        [field]: value,
      },
    }));
  };

  handleInlineEditStart = (tagId, item) => {
    // Extract duration value and unit from item data
    let durationValue = item.durationValue || "";
    let durationUnit = item.durationUnit || "Days";

    // If durationValue is empty but duration field exists, try to parse it
    if (!durationValue && item.duration) {
      const durationStr = String(item.duration).trim();
      const match = durationStr.match(/^(\d+)\s+(.+)$/);
      if (match) {
        durationValue = match[1];
        durationUnit = match[2];
      } else if (!/\d/.test(durationStr)) {
        // Duration is just a unit (e.g., "Days"), keep durationValue empty
        durationUnit = durationStr;
      }
    }

    this.setState({
      inlineEditTagId: tagId,
      inlineEditItemId: item.medicineTagDetailId,
      inlineEditDraft: {
        medicine: { name: item.medicineName, medicineId: item.medicineId },
        dose: item.doseName,
        when: item.whenName,
        durationValue,
        durationUnit,
        instructions: item.instructions || "",
      },
    });
  };

  handleInlineCancelEdit = () => {
    this.setState({
      inlineEditTagId: null,
      inlineEditItemId: null,
      inlineEditDraft: emptyItemDraft(),
    });
  };

  handleInlineDeleteItem = (tag, item) => {
    const medicineName = item.medicineName || "this medicine";
    const listName = tag?.tagName || "this list";

    swal({
      title: "Remove medicine?",
      text: `Remove ${medicineName} from ${listName}?`,
      icon: "warning",
      buttons: ["Cancel", "Remove"],
    }).then((isConfirm) => {
      if (!isConfirm) return;

      this.setState({ inlineSavingItem: true });
      MedicineTagListService.deactivateItem(item.medicineTagDetailId).then((result) => {
        this.setState({ inlineSavingItem: false });

        if (result.outcome === MedicineTagListOutcome.SUCCESS) {
          this.handleInlineCancelEdit();
          this.loadMedicineListsForInstitute(this.state.selectedInstituteId, true);
          swal({
            title: "Success!",
            text: `${medicineName} removed`,
            icon: "success",
            buttons: false,
            timer: 2000,
          });
        } else {
          swal({
            title: "Error",
            text: result.message || "Failed to remove medicine",
            icon: "error",
            buttons: false,
            timer: 2000,
          });
        }
      });
    });
  };

  handleInlineCommitItem = async () => {
    const { inlineEditDraft, inlineEditItemId, inlineEditTagId, inlineSavingItem, selectedInstituteId } = this.state;

    if (inlineSavingItem) return;

    const missing = missingFields(inlineEditDraft);
    if (missing.length > 0) return;

    const medicineId = inlineEditDraft.medicine?.medicineId;
    const doseName = inlineEditDraft.dose;
    const whenName = inlineEditDraft.when;

    const dose = this.state.doses.find((d) => d.name === doseName);
    const when = this.state.whens.find((w) => w.name === whenName);

    if (!medicineId || !dose || !when) {
      swal({
        title: "Error",
        text: "Invalid selection",
        icon: "error",
        buttons: false,
      });
      return;
    }

    this.setState({ inlineSavingItem: true });

    const result = await MedicineTagListService.updateItem(
      inlineEditTagId,
      inlineEditItemId,
      medicineId,
      dose.id,
      when.id,
      inlineEditDraft.durationValue,
      inlineEditDraft.durationUnit,
      inlineEditDraft.instructions
    );

    this.setState({ inlineSavingItem: false });

    if (result.outcome === MedicineTagListOutcome.SUCCESS) {
      this.handleInlineCancelEdit();
      this.loadMedicineListsForInstitute(selectedInstituteId, true);
    } else {
      swal({
        title: "Error",
        text: result.message || "Failed to update medicine",
        icon: "error",
        buttons: false,
        timer: 2000,
      });
    }
  };

  render() {
    const {
      institutes,
      selectedInstituteId,
      tags,
      selectedTagId,
      expandedTagIds,
      loading,
      error,
      showEditor,
      editorMode,
      editingTag,
      medicines,
      doses,
      whens,
      loadingMedicines,
      loadingDoses,
      loadingWhens,
      inlineEditTagId,
      inlineEditItemId,
      inlineEditDraft,
      inlineSavingItem,
    } = this.state;

    const hasMultipleInstitutes = institutes.length > 1;
    const selectedTag = tags.find((t) => t.medicineTagId === selectedTagId);

    return (
      <div className="d-flex" id="wrapper">
        {<Side />}
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            {<Top />}
            <div className="container-fluid">
              <div className="app-content">
                <section className="section">
                  <div className="row">
                    <Col lg="12">
                      <Tab.Container>
                        <Row>
                          <Col lg="12">
                            <div
                              id="medicineListCard"
                              className="card border-0 pb-0 collapse show"
                              data-parent="#accordion"
                            >
                              <div className="card-header flex-wrap">
                                <div className="d-flex align-items-center justify-content-between flex-wrap">
                                  <h3 className="fs-18 mb-4 header_title text-black">
                                    <Link
                                      to="/Dashboard"
                                      className="btn mr-2 btn-light"
                                    >
                                      <FaArrowLeft
                                        className="fs-18"
                                        size="2.0rem"
                                        style={{ verticalAlign: "baseline" }}
                                      />
                                    </Link>
                                    Medicine Lists
                                  </h3>
                                </div>

                                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "12px" }}>
                                  {institutes.length > 0 && (
                                    <div style={{ minWidth: "250px" }}>
                                      <Form.Control
                                        as="select"
                                        value={selectedInstituteId || ""}
                                        onChange={this.handleInstituteChange}
                                      >
                                        <option value="">
                                          {institutes.length === 1
                                            ? "Institute - Select to manage..."
                                            : "Institute - Select one..."}
                                        </option>
                                        {institutes.map((inst) => (
                                          <option
                                            key={inst.instituteId}
                                            value={inst.instituteId}
                                          >
                                            {inst.instituteName}
                                          </option>
                                        ))}
                                      </Form.Control>
                                    </div>
                                  )}

                                  <div className="d-flex" style={{ gap: "8px" }}>
                                    <Button
                                      variant="secondary"
                                      className="btn-primary"
                                      onClick={this.handleEditList}
                                      disabled={!selectedTag}
                                      title={
                                        selectedTag
                                          ? ""
                                          : "Select a list to edit"
                                      }
                                    >
                                      Edit List
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      className="btn-primary"
                                      onClick={this.handleCreateList}
                                    >
                                      <FaPlus
                                        className="mr-2"
                                        style={{ verticalAlign: "text-top" }}
                                      />
                                      Create List
                                    </Button>
                                  </div>
                                </div>
                              </div>

                              <div className="card-body p-3">
                                {loading && <Loader show={true} />}

                                {error && (
                                  <Alert variant="danger" className="mb-3">
                                    {error}
                                    {error.includes("Failed to load") && (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="ml-2"
                                        onClick={() =>
                                          this.loadMedicineListsForInstitute(
                                            selectedInstituteId
                                          )
                                        }
                                      >
                                        Retry
                                      </Button>
                                    )}
                                  </Alert>
                                )}

                                {!loading && !selectedInstituteId && hasMultipleInstitutes && (
                                  <Alert variant="info">
                                    Select an institute to manage its medicine lists.
                                  </Alert>
                                )}

                                {!loading && selectedInstituteId && tags.length === 0 && (
                                  <Alert variant="info">
                                    No medicine lists yet.{" "}
                                    <Button
                                      variant="link"
                                      onClick={this.handleCreateList}
                                      className="p-0 ml-1"
                                      style={{ textDecoration: "underline" }}
                                    >
                                      Create one now.
                                    </Button>
                                  </Alert>
                                )}

                                {!loading && tags.length > 0 && (
                                  <div className="table-responsive">
                                    <table className="table table-striped table-hover text-nowrap dataTable mt-4">
                                      <thead className="thead-light">
                                        <tr>
                                          <th style={{ width: "2%" }}></th>
                                          <th style={{ width: "3%" }}></th>
                                          <th>List Name</th>
                                          <th>Category</th>
                                          <th>Description</th>
                                          <th>Medicines</th>
                                          <th style={{ width: "5%" }}></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {tags.map((tag) => {
                                          const isExpanded = expandedTagIds.has(
                                            tag.medicineTagId
                                          );
                                          const isSelected =
                                            selectedTagId === tag.medicineTagId;

                                          return (
                                            <React.Fragment
                                              key={tag.medicineTagId}
                                            >
                                              <tr
                                                style={{
                                                  backgroundColor: isSelected
                                                    ? "#e8f4f8"
                                                    : "transparent",
                                                }}
                                              >
                                                <td>
                                                  <Form.Check
                                                    type="radio"
                                                    name="medicine-list-select"
                                                    id={`tag-radio-${tag.medicineTagId}`}
                                                    className="pl-0"
                                                    checked={isSelected}
                                                    onChange={(e) =>
                                                      e.target.checked &&
                                                      this.setState({
                                                        selectedTagId:
                                                          tag.medicineTagId,
                                                      })
                                                    }
                                                    aria-label={`Select ${tag.tagName}`}
                                                  />
                                                </td>
                                                <td>
                                                  <span
                                                    onClick={() =>
                                                      this.handleToggleExpand(
                                                        tag.medicineTagId
                                                      )
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      fontSize: "18px",
                                                    }}
                                                    title={
                                                      isExpanded
                                                        ? "Collapse"
                                                        : "Expand"
                                                    }
                                                    aria-expanded={isExpanded}
                                                    aria-controls={`items-${tag.medicineTagId}`}
                                                    role="button"
                                                    tabIndex={0}
                                                  >
                                                    {isExpanded ? (
                                                      <FaAngleUp />
                                                    ) : (
                                                      <FaAngleDown />
                                                    )}
                                                  </span>
                                                </td>
                                                <td>{tag.tagName}</td>
                                                <td>{tag.category || "—"}</td>
                                                <td
                                                  title={tag.tagDescription || ""}
                                                  style={{
                                                    maxWidth: "220px",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {tag.tagDescription || "—"}
                                                </td>
                                                <td>
                                                  {(tag.items || []).filter(
                                                    (i) => i.activeInd === "Y"
                                                  ).length || 0}
                                                </td>
                                                <td className="text-right" style={{ backgroundColor: "#e8e8e8", padding: "12px 8px" }}>
                                                  <FaTrash
                                                    onClick={() =>
                                                      this.handleRemoveList(tag)
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                      color: "#cc0000",
                                                      fontSize: "20px",
                                                      padding: "4px",
                                                      display: "inline-block",
                                                      position: "relative",
                                                      zIndex: 10,
                                                    }}
                                                    title={`Remove ${tag.tagName}`}
                                                    aria-label={`Remove ${tag.tagName}`}
                                                    role="button"
                                                    tabIndex={0}
                                                  />
                                                </td>
                                              </tr>

                                              {isExpanded && (
                                                <tr>
                                                  <td colSpan="7">
                                                    <div className="pl-4 pr-4 pb-3">
                                                      {(() => {
                                                        const isInlineEditingThisTag =
                                                          inlineEditTagId ===
                                                          tag.medicineTagId;
                                                        return (
                                                          <MedicineListItemsTable
                                                            title="Medicines"
                                                            savedRows={
                                                              tag.items || []
                                                            }
                                                            allowAddNew={false}
                                                            draft={
                                                              isInlineEditingThisTag
                                                                ? inlineEditDraft
                                                                : emptyItemDraft()
                                                            }
                                                            editingItemId={
                                                              isInlineEditingThisTag
                                                                ? inlineEditItemId
                                                                : null
                                                            }
                                                            medicines={medicines}
                                                            doses={doses}
                                                            whens={whens}
                                                            loadingMedicines={
                                                              loadingMedicines
                                                            }
                                                            loadingDoses={
                                                              loadingDoses
                                                            }
                                                            loadingWhens={
                                                              loadingWhens
                                                            }
                                                            onLoadMedicines={
                                                              this
                                                                .handleLoadMedicines
                                                            }
                                                            onLoadDoses={
                                                              this.handleLoadDoses
                                                            }
                                                            onLoadWhens={
                                                              this.handleLoadWhens
                                                            }
                                                            onCreateMedicine={
                                                              this
                                                                .handleCreateMedicine
                                                            }
                                                            onCreateDose={
                                                              this
                                                                .handleCreateDose
                                                            }
                                                            onCreateWhen={
                                                              this
                                                                .handleCreateWhen
                                                            }
                                                            onDraftChange={
                                                              this
                                                                .handleInlineDraftChange
                                                            }
                                                            onCommit={() =>
                                                              this.handleInlineCommitItem()
                                                            }
                                                            onCancelEdit={
                                                              this
                                                                .handleInlineCancelEdit
                                                            }
                                                            onEdit={(item) =>
                                                              this.handleInlineEditStart(
                                                                tag.medicineTagId,
                                                                item
                                                              )
                                                            }
                                                            onDelete={(item) =>
                                                              this.handleInlineDeleteItem(
                                                                tag,
                                                                item
                                                              )
                                                            }
                                                            disabled={true}
                                                            busy={
                                                              isInlineEditingThisTag &&
                                                              inlineSavingItem
                                                            }
                                                          />
                                                        );
                                                      })()}
                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Tab.Container>
                    </Col>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        <MedicineListEditor
          show={showEditor}
          onHide={this.handleEditorClose}
          onSave={this.handleEditorSave}
          mode={editorMode}
          tag={editingTag}
          instituteId={selectedInstituteId}
          medicines={medicines}
          doses={doses}
          whens={whens}
          loadingMedicines={loadingMedicines}
          loadingDoses={loadingDoses}
          loadingWhens={loadingWhens}
          onLoadMedicines={this.handleLoadMedicines}
          onLoadDoses={this.handleLoadDoses}
          onLoadWhens={this.handleLoadWhens}
          onCreateMedicine={this.handleCreateMedicine}
          onCreateDose={this.handleCreateDose}
          onCreateWhen={this.handleCreateWhen}
        />
      </div>
    );
  }
}
